<script lang="ts">
    import { format } from "date-fns";
    import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";
    import * as Popover from "$lib/components/ui/popover/index.js"
    import * as RadioButtonGroup from "$lib/components/ui/radio-button-group";
    import * as RadioGroup from "$lib/components/ui/radio-group/index.js";
    import * as Dialog from "$lib/components/ui/dialog";
    import * as AlertDialog from "$lib/components/ui/alert-dialog/index.js";
    import * as Avatar from "$lib/components/ui/avatar";
    import * as Tabs from "$lib/components/ui/tabs/index.js";
    import * as Resizable from "$lib/components/ui/resizable/index.js";
    import * as Table from "$lib/components/ui/table";
    import Button from "@/lib/components/ui/button/button.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { Tag, XfaiBall } from "@/Common";
    import CustomActivityTags from "@/Common/CustomActivityTags.svelte";
    import { ActivityTag } from "@/Common/ActivityTag";
    import { cn } from "@/lib/utils";
    import { Activity, type ExternalLinks } from "@/Common/ActivityFunctions";
    import { reportNames, type ActivityRecordsData, type GroupSharingOptions, type ReportKeys, type SharedActivityDetails, type Signature, type Polyline, type MapController, type XertSegment, type AfterChartZoom, type ActivityAxisType, type XAxisData, type SharedActivityRecord, type PowerType, type SharedActivity, type SharedActivityMarked } from "@/Common/constants";
    import { copyStringToClipBoard, debouncer, onKeyboardEnter, round, throttle, throttleTimeout } from "@/Util/utils";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Textarea from "@/lib/components/ui/textarea/textarea.svelte";
    import { deleteActivity as _deleteActivity, getGroupData as _getGroupData, getActivityStarredSegments, getReport, getSessionReport, getStravaSegmentData, type ActivityStarredSegmentsPromise, type GroupDataPromise, type ReportPromise } from "@/Util/api";
    import Skeleton from "@/lib/components/ui/skeleton/skeleton.svelte";
    import { fade } from "svelte/transition";
    import Am5Loader from "@/Util/Am5Loader.svelte";
    import {getGroupMetrics, getSectionInterfaceFromXertSegment, getSharedActivitiesPowerAverage, getSharedActivityPowerAverage, processRecordsForChart, type ActivityChartMpaDataPromise, type ChartController, type ProcessedDataPoint, type SectionController, type SectionInterface, type StravaSegmentController } from '@/Util/XertAm5';
    import { getContext } from "svelte";
    import type { MyFitnessStores } from "@/MyFitness/stores";
    import ActivityChartNew from "./ActivityChartNew.svelte";
    import ActivityMap from "./ActivityMap.svelte";
    import type { PaneAPI } from "paneforge";
    import { format_duration } from "@/time";
    import { ScrollArea } from "$lib/components/ui/scroll-area/index.js";
    import Separator from "@/lib/components/ui/separator/separator.svelte";
    import SectionButtonGroup from "./SectionButtonGroup.svelte";
    import GroupActivityChart from "./GroupActivityChart.svelte";
    import { quintIn, quintOut } from "svelte/easing";
    import Slider from "@/lib/components/ui/slider/slider.svelte";
    import { flip } from "svelte/animate";
    import Checkbox from "@/lib/components/ui/checkbox/checkbox.svelte";
    
    
    export let activity: { // TODO: Change this to Exercise With
        path: string,
        name: string,
        start_date: string,
        description: string,
        exerciseType: string,
        workout_id: number,
        state: string[],
        medal: number,
        type: string,
        icon: string,
        power_source: string,
        flag: boolean,
        favorite: boolean,
        group_sharing: string,
        externalLinks: ExternalLinks
        original_file_path: string,
        report_types: ReportKeys[],
        hasSessionReport: boolean,
        recordsData: ActivityRecordsData, // TODO: This already expects the polyline. Look into this.
        polyline: Polyline,
        duration: number,
    }
    export let sharedActivities: Record<string, SharedActivityDetails & {
        checked?: boolean
        metrics?: {
            rpAvg: number,
            powerAvg: number,
            wkg?: number,
            speedAvg?: number,
        }
    }>
    if (sharedActivities && Object.keys(sharedActivities).length === 0) {
        sharedActivities = null // If no shared activities, set to null
    } else if (sharedActivities) {
        // Add default values to sharedActivities
        for (const path in sharedActivities) {
            sharedActivities[path] = {
                ...sharedActivities[path],
                checked: false,
                metrics: {
                    rpAvg: 0,
                    powerAvg: 0,
                    wkg: 0,
                    speedAvg: 0,
                },
            }
        }
    }
    export let signature: Signature

    const myFitness = getContext<MyFitnessStores>('myFitness')
    const { userParams, cal_settings} = myFitness

    //@ts-ignore
    const Xert = window.Xert

    // Controller
    let openController = false 
    type ControllerTabs = "chart" | "signature"
    interface ControllerTabDetails {
        title: string,
        description: String
    }
    const controllerTabDetails: Record<ControllerTabs, ControllerTabDetails> = {
        "chart": {
            title: "Chart Settings",
            description: "Manipulate the data on the chart"
        },
        "signature": {
            title: "Fitness Signature",
            description: "Change and save your signature for this activity"
        }
    }
    let controllerTab: ControllerTabs = "chart"
    function openControllerTo(tab: ControllerTabs) {
        controllerTab = tab
        openController = true
    }

    function failedToUpdate(name: string) {
        Xert.danger(`Failed to update ${name}. Try again or contact support`)
    }

    function reRenderActivity() {
        activity = activity
    }

    // Activity Name and Description
    let activityName = activity.name
    let activityDescription = activity.description
    let isSavingActivityInfo = false
    let openDetailsPopover = false
    async function onSaveActivityInfo() {
        isSavingActivityInfo = true
        try {
            await Promise.all([
                Activity.updateName(activity, activityName),
                Activity.updateDescription(activity, activityDescription)
            ])
            reRenderActivity()
            openDetailsPopover = false
        } catch(error) {
            failedToUpdate("Activity Info")
        }
        isSavingActivityInfo = false
        
    }

    function onSharingChange(value: "group" | "squad" | "public" | "private") {
        let sharingType: GroupSharingOptions
        switch(value) {
            case ("group"):
                sharingType = "Group"
                break
            case ("squad"):
                sharingType = "Squad"
                break
            case ("public"):
                sharingType = "Public"
                break
            case ("private"):
                sharingType = "Private"
                break
        }
        Activity.updateGroupSharing(activity, sharingType).then(
            () => {
                Xert.global("Sharing Updated", null)
                reRenderActivity()
            },
            () => {
                failedToUpdate("Sharing")
                reRenderActivity()
            }
        )
    }

    function updateTags(a: typeof activity) {
        const activityTag = ActivityTag("default")    
        return [
            activityTag.exerciseType(a),
            activityTag.breakthrough(a),
            activityTag.merged(a),
            activityTag.type(a),
            activityTag.powerSource(a),
            activityTag.groupSharing(a),
        ].filter(tag => tag)
    }

    let openDownloadDialog = false
    let reportPromise: ReportPromise
    let reportSelected: ReportKeys
    async function onClickReport(reportType: ReportKeys) {
        reportSelected = reportType
        openDownloadDialog = true
        reportPromise = reportType === 'se' ? getSessionReport(activity.path) : getReport(activity.path, reportType)
    }

    let openDeleteDialog = false
    function deleteActivity() {
        _deleteActivity(activity.path).then(
            (r) => {
                window.location.href = r.redirect
            },
            () => {
                Xert.danger(`Failed to delete the activity. Try again or contact support`)
                openDeleteDialog = false
            }
        )
    }

    let contentTab: "analytics" | "fitness" | "powerDuration" = "analytics"

    let movingAverage = 5
    let groupChartPowerType: PowerType = "watts"

    // Need to keep the raw data for metric computations and moving averages
    let sharedActivitiesRaw: Awaited<GroupDataPromise>
    let sharedActivitiesOutput: Record<string, SharedActivityMarked> 
    function getGroupData(start: number = null, end: number = null) {
        _getGroupData({path: activity.path, start, end}).then(
            async (r) => {

                sharedActivitiesRaw = r 

                console.log(r)

                for (const path in sharedActivities) {
                    sharedActivities[path].checked = sharedActivities[path].range_score < 1000 || sharedActivities[path].user?.me
                }

                const filteredSharedActivitiesRaw = Object.fromEntries(
                    Object.entries(sharedActivitiesRaw).filter(([path]) => sharedActivities[path].checked)
                );

                sharedActivitiesOutput = await getSharedActivitiesPowerAverage(filteredSharedActivitiesRaw, signature, movingAverage)

                sortSharedActivities()

                return sharedActivitiesOutput
            },
            () => {
                // TODO: Handle error
            }
        )
    }

    // TODO: Maybe only run this when opening the group chart for the first time ?
    if(sharedActivities) getGroupData()

    async function sortSharedActivities() {
        sharedActivities = Object.fromEntries(
            Object.entries(sharedActivities)
                .sort(([, a], [, b]) => {
                    // Checked first, then by range_score
                    if (a.checked && !b.checked) return -1;
                    if (!a.checked && b.checked) return 1;
                })
        );
    }

    function updateGroupMetrics(path: string) {
        const { rpAvg, powerAvg, wkg, speedAvg } = getGroupMetrics(
            $cal_settings.use_moving_time, 
            sharedActivitiesRaw[path].records, 
            sharedActivities[path].user?.weight,
            selectedBounds.startData.unix_time,
            selectedBounds.endData.unix_time,
        );
        sharedActivities[path].metrics = {
            rpAvg,
            powerAvg,
            wkg,
            speedAvg
        };
    }

    async function updateGroupActivities(checked: boolean, path: string) {
        sharedActivities[path].checked = checked
        setTimeout(() => {
            if (checked === true) {
                // Add this activity to sharedActivitiesOutput
                getSharedActivityPowerAverage(sharedActivitiesRaw[path], signature, movingAverage).then((result) => {
                    sharedActivitiesOutput = {
                        ...sharedActivitiesOutput,
                        [path]: result
                    };
                    sortSharedActivities();
                });
                updateGroupMetrics(path);
            } else {
                // Remove from sharedActivitiesOutput
                if (sharedActivitiesOutput && sharedActivitiesOutput[path]) {
                    delete sharedActivitiesOutput[path];
                    sharedActivitiesOutput = {...sharedActivitiesOutput};
                    sortSharedActivities();
                }
            }
        });
    }

    let chartData: Awaited<ActivityChartMpaDataPromise>
    async function getChartData() {
        chartData = await processRecordsForChart(activity.recordsData, {...signature, carb_bias: $userParams.carb_bias}, $userParams.metric, movingAverage)
    }
    getChartData()
  
    const movingAverageDebouncer = debouncer(1000)
    let updatingChart = false
    async function changeMovingAverage(value: number[]) {
        movingAverage = value[0]
        updatingChart = true
        movingAverageDebouncer.debounce(async () => {
            const filteredSharedActivitiesRaw = Object.fromEntries(
                Object.entries(sharedActivitiesRaw).filter(([path]) => sharedActivities[path].checked)
            );
            sharedActivitiesOutput = await getSharedActivitiesPowerAverage(filteredSharedActivitiesRaw, signature, movingAverage)
            chartData = await processRecordsForChart(activity.recordsData, {...signature, carb_bias: $userParams.carb_bias}, $userParams.metric, movingAverage)
            updatingChart = false
        })
    }
    function changeGroupChartPowerType(value: PowerType) {
        groupChartPowerType = value
        groupChartController.switchPowerType(value)
    }

    let selectingSection = false
    let groupChartController: ChartController
    let activityChartController: ChartController<StravaSegmentController<ProcessedDataPoint>>
    let sectionController: SectionController
    let selectedSection: SectionInterface
    let selectedBounds: {startData: XAxisData, endData: XAxisData, startIndex: number, endIndex: number}
    let zoomingToSection = false
    async function onSelectSection(section: SectionInterface) {
        selectedBounds = sectionController.showSection(section)
        selectingSection = false
        selectedSection = section
        zoomingToSection = true
        await sectionController.zoomToSection()
        zoomingToSection = false
    }

    let chartsReady = false
    async function areChartsReady() {
        if(!sectionController && activityChartController) sectionController = activityChartController.sectionController
        if (sharedActivities && !groupChartController) { // Need to wait for groupChartController to be created
            return
        } else if (sharedActivities && groupChartController) {
            await Promise.all([
                activityChartController.chartReady,
                groupChartController.chartReady
            ])
        } else {
            await activityChartController.chartReady
        }
        mapController.hideMarkersInGroup("group")
        chartsReady = true  
    };
    $: if(activityChartController || groupChartController) areChartsReady()
    
    let starredSegmentsPromise: ActivityStarredSegmentsPromise
    function getSegments() {
        starredSegmentsPromise = getActivityStarredSegments(activity.path)
        starredSegmentsPromise.then(
            (r) => {
                // segmentData = r
                console.log(r)
            },
            () => {
                // TODO: Handle error
            }
        )
    }
    getSegments()
    
    let mapController: MapController
    let mapCollapsed = false
    let mapPane: PaneAPI;

    let periodStart = "--:--:--"
    let periodEnd = "--:--:--"

    function updatePageMetrics(chartType: typeof activeChart) {
        if(chartType === "activity") {

        } else if(chartType === "group") {
            for (const path in sharedActivities) {
                const info = sharedActivities[path];
                if (info.checked && sharedActivitiesRaw[path]?.records?.length) {
                    updateGroupMetrics(path);
                }
            }
        }
    }

    function afterZoom(startIndex: number, endIndex: number, data: ProcessedDataPoint[], _axisType: ActivityAxisType) {
        if (!(data?.length > 0)) {
            console.error('afterZoom invalid data', {data})
            return
        }

        /*
            This ensures we don't go out of bounds
            This is technically only required for the group chart when there is an activity with a distance greater then that of the current user's activity. 
            However, it may be better to just cut off other user's activities when they exceed the current user's activity distance.
        */
        startIndex = Math.min(data.length - 1, startIndex)
        endIndex = Math.min(data.length - 1, endIndex)

        const switchingAxis = (activityChartController?.getAxisType() !== _axisType) || (groupChartController?.getAxisType() !== _axisType)
        if(!zoomingToSection && (data[startIndex].unix_time != selectedBounds?.startData.unix_time || data[endIndex].unix_time != selectedBounds?.endData.unix_time) && !switchingAxis) {
            selectedSection = null
            sectionController.removeSection()
        } else {
            zoomingToSection = false
        }
        
        axisType = _axisType

        
        selectedBounds = {
            startData: data[startIndex],
            endData: data[endIndex],
            startIndex,
            endIndex,
        }

        periodStart = axisType === "time" ? format_duration(selectedBounds.startData.unix_time/1000) : Number(( selectedBounds.startData.mm / 1000000).toFixed(2)) + ' ' + $userParams.activityUnits['dist']
        periodEnd = axisType === "time" ? format_duration(selectedBounds.endData.unix_time/1000) : Number(( selectedBounds.endData.mm / 1000000).toFixed(2)) + ' ' + $userParams.activityUnits['dist']

        updatePageMetrics(activeChart)
        
    }

    let activeChart: "activity" | "group" = "activity"
    let axisType: ActivityAxisType = "time"
    let changingChart = false
    async function changeChart(value: typeof activeChart) {
        changingChart = true
        // This allows the dom to update before switching
        setTimeout(async () => {
            if(value === "activity") {
                activeChart = "activity"
                sectionController = activityChartController.sectionController
                mapController.hideMarkersInGroup("group")
                mapController.showMarkersInGroup("activity")
                await activityChartController.switchAxis(axisType)
            } else if(value === "group") {
                activeChart = "group"
                sectionController = groupChartController.sectionController
                mapController.hideMarkersInGroup("activity")
                mapController.showMarkersInGroup("group")
                await groupChartController.switchAxis(axisType)
            }
    
            if(selectedSection) {
                await onSelectSection(selectedSection)
            } else {
                const startTime = selectedBounds.startData.unix_time
                const elapsedTime = selectedBounds.endData.unix_time - startTime
    
                // This will zoom into the current section but is kind of a round about way of doing it as it has to create lines on the map and then remove them
                // TODO: Add a method to SectionController to zoom to section without creating lines
                sectionController.showSection({startTime, elapsedTime})
                await sectionController.zoomToSection()
                sectionController.removeSection()
            }

            updatePageMetrics(value)
            changingChart = false
        })
    }

    export function popoverOpen(node) {
        return {
            duration: 300,
            css: t => `
                opacity: ${t};
                transform: scale(${t});
            `,
            easing: quintOut
        };
    }
    export function popoverClose(node) {
        return {
            duration: 200,
            css: t => `
                opacity: ${t};
                transform: scale(${t});
            `,
            easing: quintIn
        };
    }

    console.log(activity, $userParams, sharedActivities)

</script>
<div class="flex flex-col gap-4">
    <div class="flex bg-background p-4 shadow w-full rounded-md mt-4 border border-border text-base text-card-foreground">
        <div>
            <div class="flex flex-wrap items-center gap-2">
                <div class="font-semibold text-2xl">{activity.name}</div>
                {#if sharedActivities}
                    {@const matchedActivities = Object.values(sharedActivities).filter(a=>!a.user.me)}
                    {@const count = Object.keys(matchedActivities).length}
                    <div class="flex">
                        {#each Object.values(matchedActivities).slice(0, count <= 4 ? 4 : 3) as shared}
                            {#if !shared["user"]["me"]}
                                <Avatar.Root class="border-white border-2 first:ml-0 -ml-3 h-8 w-8">
                                    <Avatar.Image src={shared['user']['avatar']} alt={shared['user']['display_name']} />
                                    <Avatar.Fallback>{shared['user']['display_name'][0].toUpperCase()}</Avatar.Fallback>
                                </Avatar.Root>
                            {/if}
                        {/each}
                        {#if count > 4}
                            <div class="relative flex h-8 w-8 -ml-3 shrink-0 overflow-hidden rounded-full border-white border-2 bg-secondary text-secondary-foreground text-sm"><span class="m-auto">+{Object.keys(matchedActivities).length - 3}</span></div>
                        {/if}
                    </div>    
                {/if}
            </div>
            <div class="text-lg">{format(new Date(activity.start_date), 'EEE, MMM do, yyyy h:mm aa')}</div>
            {#if activity.description} 
                <div class="text-muted-foreground mt-2">{activity.description}</div> 
            {:else}
                <div class="italic text-muted-foreground mt-2" role="button" tabindex="0" on:click={() => openDetailsPopover = true} on:keypress={(e) => {onKeyboardEnter(e, () => openDetailsPopover = true)}} >Add a description...</div>
            {/if}
            <div class="flex flex-row flex-wrap items-center gap-1 mt-4">
                {#each updateTags(activity) as tag}
                    <Tag {...tag} />
                {/each}
                <CustomActivityTags exercise={activity} />
            </div>
        </div>
        <div class="flex flex-col flex-1">
            <div class="flex self-end">
    
                <Button size="icon" variant="ghost" on:click={() => {
                        Activity.toggleFavorite(activity).then(reRenderActivity, () => {failedToUpdate("Favorite")})
                    }}
                >
                    <i class={cn("fa-star text-lg", Activity.isStarred(activity) ? "fa-solid" : "fa-light")}/>
                </Button>
    
                <Popover.Root portal="#shadcn-ui-dialog-target">
                    <Popover.Trigger asChild let:builder>
                        <Button builders={[builder]} size="icon" variant="ghost"><i class={cn("fa-flag text-lg", Activity.isFlagged(activity) ? "fa-solid" : "fa-light")}/></Button>
                    </Popover.Trigger>
                    <Popover.Content class="space-y-4 w-64">
                        <div class="space-y-1 text-sm">
                            <div class="font-bold leading-none">{Activity.isFlagged(activity) ? "Unflag this activity?" : "Flag this activity?"}</div> 
                            <div class="text-muted-foreground">This will cause a recalculation of your signature</div>
                        </div>
                        <Popover.Close asChild let:builder >
                            <div class="flex gap-2">
                                <Button builders={[builder]} size="sm" variant="outline">Cancel</Button>
                                <Button builders={[builder]} size="sm" on:click={() => {
                                        Activity.toggleFlag(activity).then(reRenderActivity, () => {failedToUpdate("Flag")})
                                    }}
                                >
                                    Continue
                                </Button>
                            </div>
                        </Popover.Close>
                    </Popover.Content>
                </Popover.Root>

                <Popover.Root portal="#shadcn-ui-dialog-target">
                    <Popover.Trigger asChild let:builder>
                        <Button builders={[builder]} size="icon" variant="ghost"><i class="fa-share-nodes fa-light text-lg"/></Button>
                    </Popover.Trigger>
                    <Popover.Content class="space-y-4 max-w-xl w-full">
                        <div class="space-y-1">
                            <div class="font-bold leading-none">Sharing Options</div> 
                            <div class="text-muted-foreground text-sm">Change who can see the activity</div>
                        </div>
                        <RadioGroup.Root value={Activity.getGroupSharingType(activity)} class="grid grid-cols-2 gap-2 max-w-xl" onValueChange={onSharingChange}>
                            <Label class="grid grid-cols-[30px,auto] min-w-min items-center gap-4 relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 text-foreground font-normal cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="share-public">
                                <i class="fa-globe fa-light text-2xl justify-self-center"/>
                                <div>
                                    <div class="leading-none">Public</div>
                                    <div class="text-muted-foreground mt-1">Anyone with the link has access.</div>
                                </div>
                                <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="public" id="share-public" />    
                            </Label>
                            <Label class="grid grid-cols-[30px,auto] min-w-min items-center gap-4 relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 text-foreground font-normal cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="share-group">
                                <i class="fa-user-group fa-light text-2xl justify-self-center"/>
                                <div>
                                    <div class="leading-none">Group</div>
                                    <div class="text-muted-foreground mt-1">Anyone in your group has access.</div>
                                </div>
                                <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="group" id="share-group" />    
                            </Label>
                            <Label class="grid grid-cols-[30px,auto] min-w-min items-center gap-4 relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 text-foreground font-normal cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="share-squad">
                                <i class="fa-home fa-light text-2xl justify-self-center"/>
                                <div>
                                    <div class="leading-none">Squad</div>
                                    <div class="text-muted-foreground mt-1">Share with members of your squad.</div>
                                </div>
                                <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="squad" id="share-squad" />    
                            </Label>
                            <Label class="grid grid-cols-[30px,auto] min-w-min items-center gap-4 relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 text-foreground font-normal cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="share-private">
                                <i class="fa-lock fa-light text-2xl justify-self-center"/>
                                <div>
                                    <div class="leading-none">Private</div>
                                    <div class="text-muted-foreground mt-1">Only you have access.</div>
                                </div>
                                <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="private" id="share-private" />    
                            </Label>
                        </RadioGroup.Root>
                        <Button class="mt-2" variant="secondary" size="sm" on:click={() => copyStringToClipBoard(window.location.href)}>Copy Link</Button>
                    </Popover.Content>
                </Popover.Root>

                <Popover.Root portal="#shadcn-ui-dialog-target" bind:open={openDetailsPopover}>
                    <Popover.Trigger asChild let:builder>
                        <Button builders={[builder]} size="icon" variant="ghost"><i class="fa-edit fa-light text-lg"/></Button>
                    </Popover.Trigger>
                    <Popover.Content class="space-y-4 max-w-xl w-full">
                        <div class="space-y-1">
                            <div class="font-bold leading-none">Activity Details</div> 
                            <div class="text-muted-foreground text-sm">Change the name and description of the activity</div>
                        </div>
                        <div class="space-y-4">
                            <div class="space-y-1">
                                <Label id="activity-name">Name</Label>
                                <Input bind:value={activityName} id="activity-name" type="text"/>
                                {#if !activityName}<div class="text-destructive text-sm">Please enter a name.</div>{/if}
                            </div>
                            <div class="space-y-1">
                                <Label id="activity-description">Description</Label>
                                <Textarea class="h-28" bind:value={activityDescription} id="activity-description"/>
                            </div>
                            <Button class="gap-2 mt-2" size="sm" disabled={!activityName || isSavingActivityInfo} on:click={onSaveActivityInfo}>
                                {#if isSavingActivityInfo} 
                                    <i class="fa-regular fa-spinner animate-spin"/> 
                                {:else}
                                    Save
                                {/if}    
                            </Button>
                        </div>
                    </Popover.Content>
                </Popover.Root>
    
                <DropdownMenu.Root portal="#shadcn-ui-dialog-target">
                    <DropdownMenu.Trigger asChild let:builder>
                        <Button builders={[builder]} size="icon" variant="ghost"><i class="fa-solid fa-ellipsis"/></Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        <DropdownMenu.Label>Actions</DropdownMenu.Label>
                        <DropdownMenu.Separator/>
                        {#if Object.keys(activity.externalLinks).length}
                            <DropdownMenu.Sub>
                                <DropdownMenu.SubTrigger class="grid grid-cols-[18px,1fr,min-content] gap-1"> <i class="fa-solid fa-eye justify-self-center"/>View On</DropdownMenu.SubTrigger>
                                <DropdownMenu.SubContent side="bottom" align="start">
                                    {#if activity.externalLinks["strava"]}<DropdownMenu.Item href={activity.externalLinks["strava"].url}>Strava</DropdownMenu.Item>{/if}
                                    {#if activity.externalLinks["garmin"]}<DropdownMenu.Item href={activity.externalLinks["garmin"].url}>Garmin</DropdownMenu.Item>{/if}
                                </DropdownMenu.SubContent>
                            </DropdownMenu.Sub>
                        {/if}
                        {#if activity.original_file_path}
                            <DropdownMenu.Item class="grid grid-cols-[18px,1fr,min-content] gap-1" href={Activity.downloadFileHref(activity)}> <i class="fa-download fa-solid justify-self-center"/>Download</DropdownMenu.Item>
                        {/if}
                        {#if activity.report_types.length}
                        <DropdownMenu.Sub>
                            <DropdownMenu.SubTrigger class="grid grid-cols-[18px,1fr,min-content] gap-1"> <i class="fa-solid fa-memo justify-self-center"/>Reports</DropdownMenu.SubTrigger>
                            <DropdownMenu.SubContent side="bottom" align="start">
                                {#if activity.report_types.includes('wr')}
                                    <DropdownMenu.Item class="grid grid-cols-[18px,1fr] gap-1" on:click={() => onClickReport('wr')}> <i class="fa-light fa-gauge-high justify-self-center" /> Workout </DropdownMenu.Item>
                                {/if}
                                {#if activity.report_types.includes('xfair')}
                                    <DropdownMenu.Item class="grid grid-cols-[18px,1fr] gap-1" on:click={() => onClickReport('xfair')}> <XfaiBall class='h-[14px] justify-self-center' /> Forecast AI </DropdownMenu.Item>
                                {/if}
                                {#if activity.report_types.includes('br')}
                                    <DropdownMenu.Item class="grid grid-cols-[18px,1fr] gap-1" on:click={() => onClickReport('br')}> <i class="fa-light fa-award justify-self-center" /> Breakthrough </DropdownMenu.Item>
                                {/if}
                                {#if activity.report_types.includes('ar')}
                                    <DropdownMenu.Item class="grid grid-cols-[18px,1fr] gap-1" on:click={() => onClickReport('ar')}> <i class="fa-light fa-chart-line justify-self-center" /> Activity </DropdownMenu.Item>
                                {/if}
                                {#if activity.hasSessionReport}
                                    <DropdownMenu.Item class="grid grid-cols-[18px,1fr] gap-1" on:click={() => onClickReport('se')}> <i class="fa-light fa-chart-line justify-self-center" /> Session </DropdownMenu.Item>
                                {/if}
                            </DropdownMenu.SubContent>
                        </DropdownMenu.Sub>
                        {/if}
                        <DropdownMenu.Item class="grid grid-cols-[18px,1fr,min-content] gap-1" on:click={() => {openDeleteDialog = true}}> <i class="fa-trash fa-solid justify-self-center"/>Delete</DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu.Root>
    
            </div>
        </div>
        
            
    </div>

    <Tabs.Root bind:value={contentTab}>
        <Tabs.List class="grid grid-cols-3">
            <Tabs.Trigger value="analytics">Analytics</Tabs.Trigger>
            <Tabs.Trigger value="fitness">Fitness</Tabs.Trigger>
            <Tabs.Trigger value="powerDuration">Power Duration</Tabs.Trigger>
        </Tabs.List>
    
        <Resizable.PaneGroup class="mt-4 rounded-md border border-border" direction="horizontal">
            <Resizable.Pane bind:pane={mapPane} collapsedSize={5} collapsible={true} minSize={25} maxSize={50} defaultSize={30} onCollapse={() => mapCollapsed = true} onExpand={() => mapCollapsed = false}>
                <div class={cn("flex justify-center items-center h-80 bg-muted opacity-25", {"hidden": !mapCollapsed})} role="button" tabindex="-1" on:click={mapPane.expand} on:keypress={()=>{}} >
                    <i class="fa-location-dot fa-solid text-2xl text-muted-foreground"/>
                </div>
                <ActivityMap class={cn("h-80", {"hidden": mapCollapsed})} mapPolyline={activity.polyline} bind:mapController/>
            </Resizable.Pane>
            <Resizable.Handle withHandle />
            <Resizable.Pane class="shadow-inner @container">
                <Tabs.Content class="mt-0 h-full relative" value="analytics">
                    {#if selectingSection}
                        <ScrollArea class="h-80 px-4 py-4">
                            <div class="text-sm text-muted-foreground leading-none">Strava Segments</div>
                            <Separator class="mt-1 mb-3"/>
                            {#await starredSegmentsPromise}
                                Loading
                            {:then segmentData}
                                {#if segmentData?.segments?.length > 0}
                                    {@const sections = segmentData.segments.map((segment) => {return getSectionInterfaceFromXertSegment(segment)})}
                                    <SectionButtonGroup {sections} sectionController={sectionController} {onSelectSection} {selectedSection}/>
                                {:else}
                                    <!-- TODO: -->
                                    No segments found. "Take action statement."
                                {/if}
                            {/await}  
                            
                            <Button class="rounded-full absolute top-2 right-3 h-8 w-8" size="icon" variant="secondary" 
                                on:click={() => {
                                    selectedSection = null
                                    selectingSection = false
                                    if(!selectedSection) sectionController.removeSection()
                                }}
                            >
                                <i class="fa fa-x fa-thin text-sm"/>
                            </Button>
                        </ScrollArea>
                    {:else}
                        <div class="flex flex-col h-80 gap-2 px-4 py-4">
                            <div class="flex justify-between items-center bg-muted rounded px-3 py-1">
                                <div>
                                    <div class="text-sm text-muted-foreground">{selectedSection ? selectedSection.name : "Period"}</div>
                                    <div>
                                        {periodStart} - {periodEnd}
                                    </div>
                                </div>
                                <div class="flex gap-2">
                                    <!-- {#if selectedSection} <Button class="rounded-full" size="icon" variant="outline" on:click={() => {segmentController.removeStravaSegmentAnalysis(); selectedSection = null;}}><i class="fa fa-x fa-thin"/></Button> {/if} -->
                                    <Button size="sm" variant="outline" on:click={() => {selectingSection = true; selectedSection = null;}}> {"Zoom to Section"} </Button>
                                </div>
                            </div>
                            <ScrollArea class="flex-1" orientation="both">
                                {#if activeChart === "group"}
                                    <Table.Root>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.Head class="w-8"></Table.Head>
                                                <Table.Head>User</Table.Head>
                                                <Table.Head>RP (W)</Table.Head>
                                                <Table.Head>Power (W)</Table.Head>
                                                <Table.Head>W/kg</Table.Head>
                                                <Table.Head>Speed ({$userParams.activityUnits.spd})</Table.Head>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {#each Object.entries(sharedActivities) as [path, info] (path)}
                                                <!-- <div class="table-row" animate:flip> -->
                                                    <Table.Row class="hover:cursor-pointer [&:has(button[data-state='unchecked'])]:opacity-50" on:click={() => updateGroupActivities(!info.checked, path)}>
                                                        <Table.Cell>
                                                            <Checkbox class={cn("pointer-events-none", !!info.user?.me && "hidden")} bind:checked={info.checked} />
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {info.user.display_name}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {info.checked ? round(info.metrics.rpAvg, 1) : "--"}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {info.checked ? round(info.metrics.powerAvg, 1) : "--"}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {info.checked ? round(info.metrics.wkg, 2) : "--"}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {info.checked ? round(info.metrics.speedAvg, 1) : "--"}
                                                        </Table.Cell>
                                                    </Table.Row>
                                                <!-- </div> -->
                                            {/each}
                                        </Table.Body>
                                    </Table.Root>
                                {/if}
                            </ScrollArea>
                            <div class="flex items-end">
                                {#if sharedActivities}
                                    <div class="flex gap-3 items-center ml-auto">
                                        {#if !chartsReady || changingChart}<i class="fa-regular fa-spinner text-base animate-spin text-muted-foreground"/>{/if}
                                        <RadioButtonGroup.Root disabled={!chartsReady || changingChart} bind:value={activeChart} onValueChange={changeChart}>
                                            <RadioButtonGroup.Button value="activity" class="flex items-center gap-1"><i class="fa-regular fa-wave-pulse"/>Activity</RadioButtonGroup.Button>
                                            <RadioButtonGroup.Button value="group" class="flex items-center gap-1"><i class="fa-regular fa-user-group"/>Group</RadioButtonGroup.Button>
                                        </RadioButtonGroup.Root>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {/if}
                    
                </Tabs.Content>
                <Tabs.Content class="mt-0 overflow-auto px-4 py-4 max-h-80" value="fitness">
                    in development.
                </Tabs.Content>
                <Tabs.Content class="mt-0 overflow-auto px-4 py-4 max-h-80" value="powerDuration">
                    in development.
                </Tabs.Content>
            </Resizable.Pane>
        </Resizable.PaneGroup>
    </Tabs.Root>

    <div class="border py-3 pl-4 pr-2 rounded-md border-border">
        <div class:hidden={activeChart !== "activity"}>
            {#if !chartData}
                <Skeleton class="h-96" />
            {:else}
                <Am5Loader>
                    <ActivityChartNew mpaData={chartData} bind:chartController={activityChartController} map={{controller: mapController, group:"activity"}} {afterZoom} allowExport startingAxisType={axisType}>
                    <Button slot="buttons" size="icon" variant="outline" on:click={() => { openController = true; controllerTab = 'chart'; }}>
                        <i class="fa-regular fa-gear" />
                    </Button>
                </ActivityChartNew>
                </Am5Loader>
            {/if}
        </div>
        
        {#if sharedActivities}
            <div class:hidden={activeChart !== "group"}>
                {#if !sharedActivitiesOutput}
                    <Skeleton class="h-96" />
                {:else}
                    <Am5Loader>
                        <GroupActivityChart sharedActivities={sharedActivitiesOutput} bind:chartController={groupChartController} map={{controller: mapController, group:"group"}} {afterZoom} startingAxisType={axisType}>
                        <Button slot="buttons" size="icon" variant="outline" on:click={() => { openController = true; controllerTab = 'chart'; }}>
                            <i class="fa-regular fa-gear" />
                        </Button>
                    </GroupActivityChart>
                </Am5Loader>
                {/if}
            </div>
        {/if}
    </div>
</div>

<!-- Controller Popover in Bottom Right -->
<div class="sticky right-10 bottom-10 items-end -mt-1 z-10 w-fit ml-auto">
    <div class="w-full h-full relative">
        <!-- Always-visible Button -->
        <Button
            variant="secondary"
            size="icon"
            class={cn(
                "absolute bottom-2 right-2 rounded-full h-12 w-12 z-50 border border-border",
                openController ? "shadow-inner" : "shadow-2xl"
            )}
            on:click={() => openController = !openController}
        >
            <i class={cn("fa-regular", openController ? "fa-x" : "fa-sliders")}/>
        </Button>

        {#if updatingChart}
            <div class="absolute top-3 right-3 z-50">
                <i class="fa-regular fa-spinner animate-spin text-muted-foreground"/>
            </div>
        {/if}
    
        <!-- Popover Overlay and Content, only visible when openController -->
        {#if openController}
            <div
                class="origin-bottom-right bg-white rounded-md rounded-br-3xl shadow-2xl px-2 pt-4 border border-border z-20"
                in:popoverOpen
                out:popoverClose
            >   
                <div class="px-2">
                    <div class="flex flex-col space-y-1 text-center sm:text-left mb-4">
                        <div class="text-foreground text-lg font-semibold leading-none">{controllerTabDetails[controllerTab].title}</div>
                        <div class="text-muted-foreground text-sm">{controllerTabDetails[controllerTab].description}</div>
                    </div>

                    {#if controllerTab === "chart"}
                        <div class="flex flex-col gap-4">

                            <div class="space-y-1">
                                <div class="text-sm leading-none">Moving Average</div>
                                <div class="flex gap-4 items-baseline">
                                    <Slider max={600} min={1} step={1} value={[movingAverage]} onValueChange={changeMovingAverage} />
                                    <div class="font-medium text-sm">{movingAverage}s</div>                            
                                </div>
                            </div>

                            {#if activeChart === "group"}
                                <div class="space-y-3">
                                    <div class="text-sm leading-none">Power Type</div>
                                    <RadioButtonGroup.Root variant="outline" selectVariant="primary" size="sm" grouped value={groupChartPowerType} 
                                        onValueChange={changeGroupChartPowerType}
                                    >
                                        <RadioButtonGroup.Button value="watts">
                                            Watts
                                        </RadioButtonGroup.Button>
                                        <RadioButtonGroup.Button value="relative">
                                            Relative
                                        </RadioButtonGroup.Button>
                                    </RadioButtonGroup.Root>   
                                </div>
                            {/if}

                        </div>
                    {/if}
                </div>
                <Separator class="mb-1 mt-3"/>
                <RadioButtonGroup.Root class="justify-end h-[3.75rem] mr-14 items-center" bind:value={controllerTab} >
                    <RadioButtonGroup.Button value="chart" class="flex items-center gap-1"><i class="fa-regular fa-chart-line"/>Chart</RadioButtonGroup.Button>
                    <RadioButtonGroup.Button value="signature" class="flex items-center gap-1"><i class="fa-regular fa-signature"/>Signature</RadioButtonGroup.Button>
                </RadioButtonGroup.Root>
            </div>
        {/if}
    </div>
</div>



<!-- Report Download Dialog -->
<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open={openDownloadDialog}>
    <Dialog.Content class="max-h-[100dvh] max-w-2xl">
        
        {#await reportPromise}
            <Dialog.Header>
                <Dialog.Title>Generating {reportNames[reportSelected]} Report</Dialog.Title>
            </Dialog.Header>
            <Skeleton class="w-full h-96" />
            <Skeleton class="h-9 w-full" />
        {:then results}
            {#if results.path}
                <div class="flex flex-col gap-4" transition:fade>
                    <img class="mt-6 h-96" src={results.path} alt="Report"/>
                    <Button variant="outline" href={results.path}>Download Report</Button>
                </div>
            {:else}
                Unable to generate report. Please try again later or contact support@xertonline.com
            {/if}
        {/await}
        
    </Dialog.Content>
</Dialog.Root>

<!-- Delete Dialog -->
<AlertDialog.Root portal="#shadcn-ui-dialog-target" bind:open={openDeleteDialog}>
    <AlertDialog.Content>
        <AlertDialog.Header>
            <AlertDialog.Title>Are you sure you want to delete this activity ?</AlertDialog.Title>
            <AlertDialog.Description>
                This will cause a trickle-down operation and a possible change to your signature.
            </AlertDialog.Description>
        </AlertDialog.Header>
        <AlertDialog.Footer>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action asChild let:builder>
                <Button builders={[builder]} variant="destructive" on:click={deleteActivity}>Delete</Button>
            </AlertDialog.Action>
        </AlertDialog.Footer>
    </AlertDialog.Content>
</AlertDialog.Root>

<!-- Download Report Dialog -->
<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open={openDownloadDialog}>
    <Dialog.Content class="max-h-[100dvh]">
        
        {#await reportPromise}
            <Dialog.Header>
                <Dialog.Title>Generating {reportNames[reportSelected]} Report</Dialog.Title>
            </Dialog.Header>
            <Skeleton class="w-full h-96" />
            <Skeleton class="h-9 w-full" />
        {:then results}
            {#if results.path}
                <div class="flex flex-col gap-4" transition:fade>
                    <img class="mt-6 h-96" src={results.path} alt="Report"/>
                    <Button variant="outline" href={results.path}>Download Report</Button>
                </div>
            {:else}
                Unable to generate report. Please try again later or contact support@xertonline.com
            {/if}
        {/await}
        
    </Dialog.Content>
</Dialog.Root>